import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BookingSlotsConfig } from '../models/Room';
import {
  BOOKING_SLOTS_DURATION_STEP_OPTIONS,
  BOOKING_SLOTS_MIN_DURATION_OPTIONS,
  BOOKING_SLOTS_START_STEP_OPTIONS,
} from '../utils/bookingSlotsConfig';

interface BookingSlotsConfigFieldsProps {
  value: BookingSlotsConfig;
  onChange: (next: BookingSlotsConfig) => void;
  /** i18n key prefix, e.g. `room` or `roomDetails` or `tutor.profileCompletion`. */
  i18nPrefix: string;
  disabled?: boolean;
  hideTitle?: boolean;
}

const BookingSlotsConfigFields: React.FC<BookingSlotsConfigFieldsProps> = ({
  value,
  onChange,
  i18nPrefix,
  disabled = false,
  hideTitle = false,
}) => {
  const { t } = useTranslation();

  const handleFieldChange = (field: keyof BookingSlotsConfig, nextValue: number) => {
    onChange({ ...value, [field]: nextValue });
  };

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {hideTitle ? null : (
        <Typography variant="subtitle1">
          {t(`${i18nPrefix}.bookingSlotsConfiguration`, 'Booking Slots Configuration')}
        </Typography>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ width: '100%' }}
      >
        <FormControl fullWidth size="small" disabled={disabled} sx={{ flex: 1, minWidth: 0 }}>
          <InputLabel>{t(`${i18nPrefix}.minimumDuration`, 'Minimum Duration')}</InputLabel>
          <Select
            value={value.minDurationInMinutes}
            label={t(`${i18nPrefix}.minimumDuration`, 'Minimum Duration')}
            onChange={(e) => handleFieldChange('minDurationInMinutes', Number(e.target.value))}
          >
            {BOOKING_SLOTS_MIN_DURATION_OPTIONS.map((val) => (
              <MenuItem key={val} value={val}>
                {val} {t(`${i18nPrefix}.minutes`, 'minutes')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" disabled={disabled} sx={{ flex: 1, minWidth: 0 }}>
          <InputLabel>{t(`${i18nPrefix}.startTimeStep`, 'Start Time Step')}</InputLabel>
          <Select
            value={value.startTimeCardinalityInMinutes}
            label={t(`${i18nPrefix}.startTimeStep`, 'Start Time Step')}
            onChange={(e) => handleFieldChange('startTimeCardinalityInMinutes', Number(e.target.value))}
          >
            {BOOKING_SLOTS_START_STEP_OPTIONS.map((val) => (
              <MenuItem key={val} value={val}>
                {val} {t(`${i18nPrefix}.minutes`, 'minutes')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" disabled={disabled} sx={{ flex: 1, minWidth: 0 }}>
          <InputLabel>{t(`${i18nPrefix}.durationStep`, 'Duration Step')}</InputLabel>
          <Select
            value={value.durationCardinalityInMinutes}
            label={t(`${i18nPrefix}.durationStep`, 'Duration Step')}
            onChange={(e) => handleFieldChange('durationCardinalityInMinutes', Number(e.target.value))}
          >
            {BOOKING_SLOTS_DURATION_STEP_OPTIONS.map((val) => (
              <MenuItem key={val} value={val}>
                {val} {t(`${i18nPrefix}.minutes`, 'minutes')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export default BookingSlotsConfigFields;
